import { useEffect, useState } from "react";
import { api, useAuth } from "../../App";
import { mono, Chip, Btn, Select, Input, Textarea, Field, PageTitle, Drawer } from "./ptUi";

// Team Allocation — the master roster: who's allocated to which brands, monthly
// planned vs delivered, delivery %, and load. Roster = Dream Machine users;
// allocation extras stored in pt_member_profiles. Mirrors the Excel sheet.

const EMPLOYMENT = ["Full-time", "Freelance", "Intern"];
const LOAD = ["Light", "Balanced", "Overloaded"];
const addMonth = (m, d) => { const [y, mo] = m.split("-").map(Number); const x = new Date(y, mo - 1 + d, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`; };

export default function PtrackerAllocation() {
  const { user } = useAuth();
  const isLead = ["admin", "srmanager", "manager", "creativedirector", "associatecreativedirector"].includes((user?.role || "").toLowerCase());
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [drawer, setDrawer] = useState(null);

  const load = () => api.get(`/ptracker/allocation?month=${month}`).then((r) => setRows(r.data.allocation || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => { api.get("/ptracker/refs/lists").then((r) => setBrands(r.data.brands || [])).catch(() => {}); }, []);

  const th = { textAlign: "left", fontFamily: mono, fontSize: 10, fontWeight: 700, color: "var(--t3)", letterSpacing: ".06em", textTransform: "uppercase", padding: "8px 10px" };
  const td = { padding: "8px 10px", fontSize: 13, color: "var(--t1)", verticalAlign: "middle" };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1200, margin: "0 auto" }} data-testid="ptracker-allocation">
      <PageTitle title="Team Allocation" sub="The master roster — brands, monthly output and load. People come from Dream Machine."
        right={
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span onClick={() => setMonth(addMonth(month, -1))} style={{ cursor: "pointer", color: "var(--t2)", padding: "6px 10px", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)" }}>‹</span>
            <span style={{ fontFamily: mono, fontSize: 12, color: "var(--t1)", minWidth: 90, textAlign: "center" }}>{month}</span>
            <span onClick={() => setMonth(addMonth(month, 1))} style={{ cursor: "pointer", color: "var(--t2)", padding: "6px 10px", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)" }}>›</span>
          </div>
        } />

      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--s2)" }}>
            <tr><th style={th}>Name</th><th style={th}>Dept</th><th style={th}>Employment</th><th style={th}>Brands</th><th style={{ ...th, textAlign: "right" }}>Planned</th><th style={{ ...th, textAlign: "right" }}>Delivered</th><th style={{ ...th, textAlign: "right" }}>Deliv %</th><th style={th}>Load</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} style={{ borderTop: "1px solid var(--bdr)", cursor: isLead ? "pointer" : "default" }} onClick={() => isLead && setDrawer(r)}>
                <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{r.department || "—"}</td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t2)" }}>{r.employment}</td>
                <td style={{ ...td, color: "var(--t2)", maxWidth: 240, fontSize: 12 }}>{(r.brands || []).join(", ") || "—"}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: mono }}>{r.planned}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: mono, color: "#0ca30c" }}>{r.delivered}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: mono, fontWeight: 700 }}>{r.delivery_pct}%</td>
                <td style={td} onClick={(e) => e.stopPropagation()}><Chip value={r.load} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", fontFamily: mono, fontSize: 13, color: "var(--t3)" }}>Loading roster…</td></tr>}
          </tbody>
        </table>
      </div>

      {drawer && <AllocForm row={drawer} brands={brands} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />}
    </div>
  );
}

function AllocForm({ row, brands, onClose, onSaved }) {
  const [f, setF] = useState({ employment: row.employment || "Full-time", load: row.load || "Balanced", brands: [...(row.brands || [])], notes: row.notes || "" });
  const [saving, setSaving] = useState(false);
  const toggleBrand = (b) => setF((p) => ({ ...p, brands: p.brands.includes(b) ? p.brands.filter((x) => x !== b) : [...p.brands, b] }));
  const save = () => { setSaving(true); api.put(`/ptracker/allocation/${row.user_id}`, f).then(onSaved).catch((e) => { alert(e.response?.data?.detail || "Save failed"); setSaving(false); }); };
  return (
    <Drawer title={`Allocation — ${row.name}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{row.department} · this month: {row.delivered}/{row.planned} delivered ({row.delivery_pct}%)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Employment"><Select options={EMPLOYMENT} value={f.employment} onChange={(e) => setF((p) => ({ ...p, employment: e.target.value }))} /></Field>
          <Field label="Load"><Select options={LOAD} value={f.load} onChange={(e) => setF((p) => ({ ...p, load: e.target.value }))} /></Field>
        </div>
        <Field label="Brands / projects allocated">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", padding: 10, maxHeight: 200, overflowY: "auto" }}>
            {brands.length === 0 && <span style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", gridColumn: "1/3" }}>No brands yet.</span>}
            {brands.map((b) => (
              <label key={b} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--t1)", cursor: "pointer" }}>
                <input type="checkbox" checked={f.brands.includes(b)} onChange={() => toggleBrand(b)} />{b}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} /></Field>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Btn></div>
      </div>
    </Drawer>
  );
}
